import dayjs, { Dayjs } from 'dayjs'
import type { Ref } from 'vue'
import { $t } from '../../../../composables/i18n'
import { usePrevioDS } from '../../../../composables/helpers'

export const useDateTimeRangeValidator = (
  model: Ref<Dayjs | null>,
  includeTime: boolean,
  min?: Dayjs,
  max?: Dayjs,
) => {
  return (): string | boolean => {
    if (!min && !max) {
      return true
    }

    const userFormat = includeTime
      ? usePrevioDS().useFormat().dateTime.NICE_DATE_TIME
      : usePrevioDS().useFormat().dateTime.NICE_DATE

    if (!model.value && (min || max)) {
      return $t('The value cannot be empty')
    }

    if (!model.value?.isValid()) {
      return $t('Selected date time is invalid')
    }

    if (min && max) {
      return model.value?.tz().isSameOrAfter(min.tz()) && model.value?.tz().isSameOrBefore(max.tz())
        ? true
        : $t('Selected date and time has to be between {min} and {max}', {
            min: min.tz().format(userFormat),
            max: max.tz().format(userFormat),
          })
    }

    if (min) {
      return model.value?.tz().isSameOrAfter(min.tz())
        ? true
        : $t('Selected date and time cannot be earlier than {time}', {
            time: min.tz().format(userFormat),
          })
    }

    if (max) {
      return model.value?.tz().isSameOrBefore(max.tz())
        ? true
        : $t('Selected date and time cannot be later than {time}', {
            time: max.tz().format(userFormat),
          })
    }

    return true
  }
}

export const useTimeRangeValidator = (model: Ref<Dayjs | null>, min?: Dayjs, max?: Dayjs) => {
  return (): string | boolean => {
    if (!min && !max) {
      return true
    }

    if (!model.value && (min || max)) {
      return $t('The value cannot be empty')
    }

    if (!model.value?.isValid()) {
      return $t('Time has to be in format {format}', {
        format: 'HH:mm',
      })
    }

    const inputDateTimeNormalized = model.value.tz().clone().second(0).millisecond(0)

    if (min && max) {
      const minDateTimeNormalized = min.tz().second(0).millisecond(0)
      const maxDateTimeNormalized = max.tz().second(0).millisecond(0)

      return (
        (inputDateTimeNormalized.isSameOrAfter(minDateTimeNormalized) &&
          inputDateTimeNormalized.isSameOrBefore(maxDateTimeNormalized)) ||
        $t('Selected time has to be between {min} and {max}', {
          min: min.format('HH:mm'),
          max: max.format('HH:mm'),
        })
      )
    }

    if (min) {
      const minDateTimeNormalized = dayjs()
        .tz()
        .hour(min.hour())
        .minute(min.minute())
        .second(0)
        .millisecond(0)

      return (
        inputDateTimeNormalized.isSameOrAfter(minDateTimeNormalized) ||
        $t('Selected time cannot be less than {time}', {
          time: min.tz().format('HH:mm'),
        })
      )
    }

    return true
  }
}
